import { useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { lean } from "@/content";
import { Rich } from "@/lib/rich";

const losses = lean.vision.losses;

export default function LeanVision() {
  const { ref, revealed } = useScrollReveal();
  const [active, setActive] = useState<string | null>(null);

  return (
    <section id="vision" className="py-24 relative overflow-hidden bg-gradient-to-b from-[#FAFAF8] to-white dark:from-[#0A0A0A] dark:to-[#0C0C0E]">
      <div className="absolute top-[20%] right-[-10%] w-[450px] h-[450px] rounded-full bg-orange-100/40 blur-[140px] dark:bg-orange-500/10 pointer-events-none" />
      <div className="absolute bottom-[10%] left-[-5%] w-[300px] h-[300px] rounded-full bg-amber-100/30 blur-[100px] dark:bg-amber-500/[0.08] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 dark:bg-orange-500/[0.12] dark:border-orange-400/25 dark:text-orange-300 text-xs font-semibold tracking-widest uppercase mb-6">
            {lean.vision.badge}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-stone-900 leading-tight mb-4 dark:text-white">
            <Rich text={lean.vision.title} accentClassName="text-orange-600 dark:text-orange-500" />
          </h2>
          <p className="text-stone-600 text-lg leading-relaxed max-w-2xl dark:text-white/60 mx-auto">
            {lean.vision.subtitle}
          </p>
        </div>

        {/* Interactive junction */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-4 mb-12">
          {/* Robot side */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-stone-100 border border-stone-200 dark:bg-white/[0.06] dark:border-white/[0.09]">
              <i className="ri-robot-2-line text-stone-600 text-3xl dark:text-white/60"></i>
            </div>
            <span className="text-stone-600 text-xs font-semibold uppercase tracking-wider dark:text-white/60">{lean.vision.labels.digital}</span>
          </div>

          {/* Crack / Lightning */}
          <div className="relative flex-1 max-w-xs lg:max-w-sm h-16 flex items-center justify-center">
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-stone-300 via-orange-400 to-stone-300 dark:from-white/[0.14] dark:via-orange-500/60 dark:to-white/[0.14]" />
            <button
              onClick={() => setActive(active === "crack" ? null : "crack")}
              className="relative z-10 w-12 h-12 flex items-center justify-center rounded-full bg-orange-600 text-white hover:bg-orange-700 transition-colors cursor-pointer shadow-lg dark:bg-orange-500 dark:text-[#0A0A0A] dark:hover:bg-orange-400"
              aria-label={lean.vision.labels.crackButton}
            >
              <i className="ri-flashlight-line text-lg"></i>
            </button>
          </div>

          {/* Human side */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-stone-100 border border-stone-200 dark:bg-white/[0.06] dark:border-white/[0.09]">
              <i className="ri-user-settings-line text-stone-600 text-3xl dark:text-white/60"></i>
            </div>
            <span className="text-stone-600 text-xs font-semibold uppercase tracking-wider dark:text-white/60">{lean.vision.labels.operator}</span>
          </div>
        </div>

        {/* Loss cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {losses.map((loss, i) => (
            <LossCard key={loss.id} loss={loss} index={i} isActive={active === "crack" || active === loss.id} onHover={() => setActive(loss.id)} onLeave={() => setActive(null)} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LossCard({
  loss,
  index,
  isActive,
  onHover,
  onLeave,
}: {
  loss: { id: string; label: string; desc: string; icon: string };
  index: number;
  isActive: boolean;
  onHover: () => void;
  onLeave: () => void;
}) {
  const { ref, revealed } = useScrollReveal();

  return (
    <div
      ref={ref}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      className={`relative bg-white border rounded-2xl p-6 dark:bg-white/[0.04] transition-all duration-500 cursor-pointer ${
        isActive
          ? "border-orange-400 shadow-[0_8px_30px_rgba(234,88,12,0.08)] scale-[1.02] dark:border-orange-500/50 dark:shadow-[0_8px_30px_rgba(249,115,22,0.08)]"
          : "border-stone-200 shadow-none dark:border-white/[0.08]"
      } ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-4 transition-colors ${isActive ? "bg-orange-50 dark:bg-orange-500/10" : "bg-stone-100 dark:bg-white/[0.06]"}`}>
        <i className={`${loss.icon} text-lg transition-colors ${isActive ? "text-orange-600 dark:text-orange-400" : "text-stone-600 dark:text-white/60"}`}></i>
      </div>
      <h4 className="text-stone-900 font-bold text-sm dark:text-white mb-1">{loss.label}</h4>
      <p className="text-stone-600 text-xs leading-relaxed dark:text-white/60">{loss.desc}</p>
    </div>
  );
}